import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { NgpSelectPortal } from 'ng-primitives/select';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  heroArrowDownTray,
  heroArrowUpTray,
  heroBanknotes,
  heroChevronDown,
  heroChevronLeft,
  heroChevronRight,
  heroChevronUp,
  heroChevronUpDown,
  heroGlobeAlt,
  heroMagnifyingGlass,
  heroPencilSquare,
  heroPlus,
  heroTrash,
  heroXCircle,
} from '@ng-icons/heroicons/outline';
import {
  ListSelection,
  ToastrService,
  UiBadge,
  UiBulkBar,
  UiButton,
  UiCheckbox,
  UiConfirmDialogService,
  UiDataTable,
  UiDataTableCell,
  UiDataTableHead,
  UiDataTableHeaderCell,
  UiDataTableRow,
  UiDataTableShell,
  UiEmptyState,
  UiFilterBar,
  UiFilterCluster,
  UiFilterField,
  UiFilterLabel,
  UiHint,
  UiInput,
  UiPageHeader,
  UiResizableColumns,
  UiSelect,
  UiSelectDropdown,
  UiSelectOption,
  UiSkeletonGrid,
  UiSortHeader,
} from '../../../shared/ui';
import type { UiBulkAction, UiBulkActionEvent, UiSortChange } from '../../../shared/ui';
import { SalesApiService, type SalesSearchField, type SaleSortBy } from './sales-api.service';
import type { SaleListItem, SaleStatus } from './sales.types';

type StatusFilter = SaleStatus | 'all';
type TypeFilter = 'all' | 'one_time' | 'recurring';
type BadgeTone = 'success' | 'warning' | 'danger' | 'neutral' | 'accent';

const SEARCH_FIELD_LABEL_PT_BR: Record<SalesSearchField, string> = {
  contact_name: 'Nome',
  contact_email: 'Email',
  contact_phone: 'Telefone',
  product_name: 'Produto',
  external_id: 'ID externo / Provedor',
};

const SEARCH_FIELD_PLACEHOLDER: Record<SalesSearchField, string> = {
  contact_name: 'Buscar pelo nome do contato…',
  contact_email: 'Buscar pelo email do contato…',
  contact_phone: 'Buscar pelo telefone do contato…',
  product_name: 'Buscar pelo nome do produto…',
  external_id: 'ID externo / ID do provedor…',
};

const STATUS_LABEL_PT_BR: Record<SaleStatus, string> = {
  draft: 'Rascunho',
  pending: 'Pendente',
  active: 'Ativa',
  trialing: 'Em trial',
  paused: 'Pausada',
  past_due: 'Em atraso',
  cancelled: 'Cancelada',
  refunded: 'Reembolsada',
  expired: 'Expirada',
};

@Component({
  selector: 'app-vendas-lista-page',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    NgIcon,
    NgpSelectPortal,
    UiBadge,
    UiBulkBar,
    UiButton,
    UiCheckbox,
    UiDataTable,
    UiDataTableCell,
    UiDataTableHead,
    UiDataTableHeaderCell,
    UiDataTableRow,
    UiDataTableShell,
    UiResizableColumns,
    UiEmptyState,
    UiFilterBar,
    UiFilterCluster,
    UiFilterField,
    UiFilterLabel,
    UiHint,
    UiInput,
    UiPageHeader,
    UiSelect,
    UiSelectDropdown,
    UiSelectOption,
    UiSkeletonGrid,
    UiSortHeader,
  ],
  providers: [
    provideIcons({
      heroArrowDownTray,
      heroArrowUpTray,
      heroBanknotes,
      heroChevronDown,
      heroChevronLeft,
      heroChevronRight,
      heroChevronUp,
      heroChevronUpDown,
      heroGlobeAlt,
      heroMagnifyingGlass,
      heroPencilSquare,
      heroPlus,
      heroTrash,
      heroXCircle,
    }),
  ],
  template: `
    <section class="catalog-shell">
      <app-ui-page-header
        title="Vendas"
        subtitle="Acompanhe vendas avulsas e assinaturas, manuais ou via provedores externos."
        [divider]="false"
      >
        <div actions class="flex flex-wrap gap-2">
          <a appButton variant="secondary" routerLink="/vendas/importar" aria-label="Importar vendas via CSV">
            <ng-icon name="heroArrowUpTray" size="16" /> Importar CSV
          </a>
          <a appButton variant="primary" routerLink="/vendas/criar" aria-label="Criar nova venda">
            <ng-icon name="heroPlus" size="18" /> Nova venda
          </a>
        </div>
      </app-ui-page-header>

      <div appUiFilterBar>
        <div appUiFilterCluster>
          <div appUiFilterField class="min-w-0 w-40">
            <span appUiFilterLabel>Buscar por</span>
            <div
              appSelect
              [ngpSelectValue]="searchField()"
              (ngpSelectValueChange)="setSearchField($event)"
              aria-label="Campo de busca"
            >
              <span>{{ searchFieldLabel() }}</span>
              <div *ngpSelectPortal appSelectDropdown>
                <div appSelectOption [ngpSelectOptionValue]="'contact_name'">Nome</div>
                <div appSelectOption [ngpSelectOptionValue]="'contact_email'">Email</div>
                <div appSelectOption [ngpSelectOptionValue]="'contact_phone'">Telefone</div>
                <div appSelectOption [ngpSelectOptionValue]="'product_name'">Produto</div>
                <div appSelectOption [ngpSelectOptionValue]="'external_id'">ID externo / Provedor</div>
              </div>
            </div>
          </div>

          <label appUiFilterField class="min-w-0">
            <span appUiFilterLabel>Busca</span>
            <span class="search-field__input">
              <ng-icon name="heroMagnifyingGlass" size="16" aria-hidden="true" />
              <input
                appInput
                surface="panel"
                type="search"
                class="w-64"
                [placeholder]="searchPlaceholder()"
                [ngModel]="search()"
                (ngModelChange)="onSearchChange($event)"
                aria-label="Buscar vendas"
              />
            </span>
          </label>

          <div appUiFilterField class="min-w-0 w-40">
            <span appUiFilterLabel>Status</span>
            <div
              appSelect
              [ngpSelectValue]="statusFilter()"
              (ngpSelectValueChange)="setStatus($event)"
              aria-label="Filtrar por status"
            >
              <span>{{ statusFilterLabel() }}</span>
              <div *ngpSelectPortal appSelectDropdown>
                <div appSelectOption [ngpSelectOptionValue]="'all'">Todos status</div>
                @for (s of statusKeys; track s) {
                  <div appSelectOption [ngpSelectOptionValue]="s">{{ statusLabel(s) }}</div>
                }
              </div>
            </div>
          </div>

          <div appUiFilterField class="min-w-0 w-40">
            <span appUiFilterLabel>Tipo</span>
            <div
              appSelect
              [ngpSelectValue]="typeFilter()"
              (ngpSelectValueChange)="setTypeFilter($event)"
              aria-label="Filtrar por tipo"
            >
              <span>{{ typeFilterLabel() }}</span>
              <div *ngpSelectPortal appSelectDropdown>
                <div appSelectOption [ngpSelectOptionValue]="'all'">Todos tipos</div>
                <div appSelectOption [ngpSelectOptionValue]="'one_time'">Venda avulsa</div>
                <div appSelectOption [ngpSelectOptionValue]="'recurring'">Assinatura</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      @if (searchHint(); as hint) {
        <p appUiHint role="status">{{ hint }}</p>
      }

      @if (loading() && visibleSales().length === 0) {
        <div appUiDataTableShell class="table-panel" aria-busy="true" aria-label="Carregando vendas">
          <div class="table-empty">
            <app-ui-skeleton-grid [rows]="3" [rowHeight]="42" />
          </div>
        </div>
      } @else if (visibleSales().length === 0) {
        <app-ui-empty-state
          icon="heroBanknotes"
          title="Nenhuma venda encontrada"
          description="Crie a primeira venda manualmente ou importe um CSV."
        >
          <a actions appButton variant="secondary" routerLink="/vendas/importar">
            <ng-icon name="heroArrowUpTray" size="16" /> Importar CSV
          </a>
          <a actions appButton variant="primary" routerLink="/vendas/criar">
            <ng-icon name="heroPlus" size="16" /> Nova venda
          </a>
        </app-ui-empty-state>
      } @else {
        @if (allOnPageSelected() && !selection.allMatching() && total() > visibleSales().length) {
          <div class="select-all-banner">
            <span>As {{ visibleSales().length }} desta página estão selecionadas.</span>
            <button appButton variant="ghost" size="sm" type="button" (click)="selectAllMatching()">
              Selecionar todas as {{ total() }} do filtro
            </button>
          </div>
        }
        <div appUiDataTableShell class="overflow-x-auto">
          <table appUiDataTable appUiResizableColumns key="revenue-sales">
            <thead appUiDataTableHead>
              <tr>
                <th appUiDataTableHeaderCell class="w-10" data-no-resize>
                  <button appCheckbox [ngpCheckboxChecked]="allOnPageSelected()" (ngpCheckboxCheckedChange)="selectAllOnPage($any($event))" aria-label="Selecionar todos"></button>
                </th>
                <th appUiDataTableHeaderCell appUiSortHeader sortKey="status" [activeKey]="sortBy()" [direction]="sortOrder()" (sortChange)="onColumnSort($event)" #statusSort="uiSortHeader">
                  <span class="th-inner">Status <ng-icon [name]="statusSort.iconName()" size="12" /></span>
                </th>
                <th appUiDataTableHeaderCell>Lead</th>
                <th appUiDataTableHeaderCell>Produto</th>
                <th appUiDataTableHeaderCell appUiSortHeader sortKey="type" [activeKey]="sortBy()" [direction]="sortOrder()" (sortChange)="onColumnSort($event)" #typeSort="uiSortHeader">
                  <span class="th-inner">Tipo <ng-icon [name]="typeSort.iconName()" size="12" /></span>
                </th>
                <th appUiDataTableHeaderCell class="center" appUiSortHeader sortKey="total" [activeKey]="sortBy()" [direction]="sortOrder()" (sortChange)="onColumnSort($event)" #totalSort="uiSortHeader">
                  <span class="th-inner th-inner--center">Total <ng-icon [name]="totalSort.iconName()" size="12" /></span>
                </th>
                <th appUiDataTableHeaderCell appUiSortHeader sortKey="created_at" [activeKey]="sortBy()" [direction]="sortOrder()" (sortChange)="onColumnSort($event)" #createdSort="uiSortHeader">
                  <span class="th-inner">Criada em <ng-icon [name]="createdSort.iconName()" size="12" /></span>
                </th>
                <th appUiDataTableHeaderCell class="actions-col" data-no-resize>Ações</th>
              </tr>
            </thead>
            <tbody>
              @for (sale of visibleSales(); track sale.id) {
                <tr appUiDataTableRow>
                  <td appUiDataTableCell>
                    <button appCheckbox [ngpCheckboxChecked]="isSelected(sale.id)" (ngpCheckboxCheckedChange)="toggleSelect(sale.id)" [attr.aria-label]="'Selecionar venda'"></button>
                  </td>
                  <td appUiDataTableCell>
                    <span appBadge [tone]="saleStatusTone(sale.status)" size="xs" class="status-badge">
                      {{ statusLabel(sale.status) }}
                    </span>
                  </td>
                  <td appUiDataTableCell>
                    <a [routerLink]="['/crm/contatos']" [queryParams]="{ open: sale.contactId }" class="link-cell">{{ sale.contactName || sale.contactId.slice(0, 8) }}</a>
                  </td>
                  <td appUiDataTableCell>
                    <a [routerLink]="['/produtos', sale.productId]" class="link-cell">{{ sale.productName || sale.productId.slice(0, 8) }}</a>
                  </td>
                  <td appUiDataTableCell class="cell-muted">
                    {{ sale.type === 'recurring' ? 'Assinatura' : 'Avulsa' }}
                    @if (sale.managementType === 'provider_managed') {
                      <span
                        appBadge
                        tone="accent"
                        size="xs"
                        class="provider-chip"
                        [attr.title]="'Gerenciada pelo provedor' + (sale.externalProvider ? ' ' + sale.externalProvider : '')"
                      >
                        <ng-icon name="heroGlobeAlt" size="11" /> {{ sale.externalProvider || 'externo' }}
                      </span>
                    }
                  </td>
                  <td appUiDataTableCell class="center mono">{{ formatMoney(sale.total, sale.currency) }}</td>
                  <td appUiDataTableCell class="cell-muted">{{ formatDate(sale.createdAt) }}</td>
                  <td appUiDataTableCell class="actions-col">
                    @if (sale.managementType === 'platform_managed') {
                      <div class="row-actions">
                        @if (canEdit(sale)) {
                          <a
                            appButton
                            variant="ghost"
                            size="sm"
                            [routerLink]="['/vendas', sale.id, 'editar']"
                            [attr.aria-label]="'Editar venda'"
                            title="Editar"
                          >
                            <ng-icon name="heroPencilSquare" size="14" />
                          </a>
                        }
                        @if (canDelete(sale)) {
                          <button
                            appButton
                            variant="ghost"
                            size="sm"
                            type="button"
                            (click)="confirmDelete(sale)"
                            aria-label="Excluir rascunho"
                            title="Excluir rascunho"
                          >
                            <ng-icon name="heroTrash" size="14" />
                          </button>
                        } @else if (canCancel(sale)) {
                          <button
                            appButton
                            variant="ghost"
                            size="sm"
                            type="button"
                            (click)="confirmCancel(sale)"
                            aria-label="Cancelar venda"
                            title="Cancelar"
                          >
                            <ng-icon name="heroXCircle" size="14" />
                          </button>
                        }
                      </div>
                    } @else {
                      <span appUiHint title="Venda gerenciada externamente — edição via provedor">—</span>
                    }
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>

        <nav class="pagination" aria-label="Paginação de vendas">
          <span class="pagination__range">{{ pageRangeLabel() }}</span>
          <div class="pagination__actions">
            <button
              appButton
              variant="secondary"
              size="sm"
              type="button"
              [disabled]="!hasPrev() || loading()"
              (click)="goToPrevPage()"
              aria-label="Página anterior"
            >
              <ng-icon name="heroChevronLeft" size="14" /> Anterior
            </button>
            <span class="pagination__page">Pág. {{ page() }} de {{ totalPages() || 1 }}</span>
            <button
              appButton
              variant="secondary"
              size="sm"
              type="button"
              [disabled]="!hasNext() || loading()"
              (click)="goToNextPage()"
              aria-label="Próxima página"
            >
              Próxima <ng-icon name="heroChevronRight" size="14" />
            </button>
          </div>
        </nav>
      }

      <app-ui-bulk-bar
        [count]="selectionCount()"
        [actions]="bulkActions"
        ariaLabel="Ações em massa de vendas"
        countLabel="selecionada(s)"
        (action)="onBulkAction($event)"
        (clear)="selection.clear()"
      />
    </section>
  `,
  styles: [
    `
      .catalog-shell {
        display: flex;
        flex-direction: column;
        gap: 1.25rem;
        padding-block: 1.5rem 3rem;
        padding-inline: var(--shell-pad-x);
        min-height: 100%;
      }

      .search-field {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
        min-width: 0;
      }

      .search-field__input {
        position: relative;
        display: block;
      }

      .search-field__input ng-icon {
        position: absolute;
        left: 0.7rem;
        top: 50%;
        transform: translateY(-50%);
        color: var(--color-muted);
        pointer-events: none;
      }

      .search-field__input input {
        padding-left: 2.1rem;
        width: 100%;
      }

      .table-panel {
        overflow: hidden;
      }

      .table-empty {
        padding: 3rem 1rem;
        text-align: center;
        color: var(--color-muted);
        font-size: 0.9rem;
      }

      .sortable-th {
        cursor: pointer;
        user-select: none;
        transition: color 120ms ease;
      }

      .sortable-th:hover {
        color: var(--color-ink);
      }

      .th-inner {
        display: inline-flex;
        align-items: center;
        gap: 0.25rem;
      }

      .th-inner--center {
        justify-content: center;
      }

      .sortable-th[aria-sort='ascending'] ng-icon,
      .sortable-th[aria-sort='descending'] ng-icon {
        color: var(--color-accent-strong);
      }

      .num {
        text-align: right;
      }

      .center {
        text-align: center;
      }

      .actions-col {
        width: 5rem;
        text-align: center;
      }

      .row-actions {
        display: inline-flex;
        gap: 0.2rem;
        justify-content: center;
      }

      .provider-chip {
        display: inline-flex;
        align-items: center;
        gap: 0.2rem;
        margin-left: 0.4rem;
        text-transform: lowercase;
      }

      .mono {
        font-variant-numeric: tabular-nums;
        font-family: var(--font-brand);
      }

      .cell-muted {
        color: var(--color-muted);
      }

      .link-cell {
        color: var(--color-accent-strong);
        text-decoration: none;
      }

      .link-cell:hover {
        text-decoration: underline;
      }

      .status-badge {
        text-transform: none;
      }

      .pagination {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 0.75rem;
        padding: 0.5rem 0.25rem 0;
      }

      .pagination__range {
        font-size: 0.78rem;
        color: var(--color-muted);
      }

      .pagination__actions {
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }

      .pagination__page {
        font-size: 0.78rem;
        color: var(--color-muted);
        min-width: 7rem;
        text-align: center;
      }

      .select-all-banner {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 0.5rem;
        padding: 0.5rem 0.75rem;
        border: 1px solid var(--color-line);
        border-radius: var(--radius-md);
        background: var(--color-accent-soft, var(--color-soft));
        font-size: 0.82rem;
        color: var(--color-ink);
      }

    `,
  ],
})
export class VendasListaPage {
  private readonly api = inject(SalesApiService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly toastr = inject(ToastrService);
  private readonly confirmDialog = inject(UiConfirmDialogService);

  readonly statusKeys: SaleStatus[] = [
    'draft',
    'pending',
    'active',
    'trialing',
    'paused',
    'past_due',
    'cancelled',
    'refunded',
    'expired',
  ];

  readonly loading = signal(false);
  readonly sales = signal<SaleListItem[]>([]);
  readonly search = signal('');
  readonly searchField = signal<SalesSearchField>('contact_name');
  readonly statusFilter = signal<StatusFilter>('all');
  readonly typeFilter = signal<TypeFilter>('all');

  readonly sortBy = signal<SaleSortBy>('created_at');
  readonly sortOrder = signal<'asc' | 'desc'>('desc');

  readonly page = signal(1);
  readonly pageSize = signal(25);
  readonly total = signal(0);
  readonly totalPages = signal(0);
  readonly hasNext = signal(false);
  readonly hasPrev = signal(false);

  readonly visibleSales = computed(() => this.sales());

  readonly selection = new ListSelection();
  readonly selectionCount = computed(() =>
    this.selection.allMatching() ? this.total() : this.selection.count(),
  );
  readonly bulkActions: UiBulkAction[] = [
    { id: 'export', label: 'Exportar CSV', icon: 'heroArrowDownTray' },
    { id: 'cancel', label: 'Cancelar', icon: 'heroXCircle', tone: 'danger' },
  ];
  readonly allOnPageSelected = computed(() => {
    const ids = this.sales().map((s) => s.id);
    return ids.length > 0 && ids.every((id) => this.selection.isSelected(id));
  });

  readonly pageRangeLabel = computed(() => {
    const t = this.total();
    if (t === 0) return '0 de 0';
    const start = (this.page() - 1) * this.pageSize() + 1;
    const end = Math.min(start + this.sales().length - 1, t);
    return `${start}–${end} de ${t}`;
  });

  readonly searchFieldLabel = computed(() => SEARCH_FIELD_LABEL_PT_BR[this.searchField()]);
  readonly searchPlaceholder = computed(() => SEARCH_FIELD_PLACEHOLDER[this.searchField()]);

  readonly searchHint = computed(() => {
    const term = this.search().trim();
    if (term.length > 0 && term.length < 2) return 'Digite ao menos 2 caracteres para buscar.';
    return null;
  });

  readonly statusFilterLabel = computed(() => {
    const v = this.statusFilter();
    return v === 'all' ? 'Todos status' : STATUS_LABEL_PT_BR[v];
  });

  readonly typeFilterLabel = computed(() => {
    const v = this.typeFilter();
    if (v === 'all') return 'Todos tipos';
    return v === 'recurring' ? 'Assinatura' : 'Avulsa';
  });

  private searchDebounce: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.reload();
  }

  onSearchChange(value: string): void {
    this.search.set(value);
    if (this.searchDebounce) clearTimeout(this.searchDebounce);
    const term = value.trim();
    if (term.length > 0 && term.length < 2) return;
    this.searchDebounce = setTimeout(() => this.resetAndReload(), 300);
  }

  setSearchField(value: unknown): void {
    this.searchField.set(value as SalesSearchField);
    if (this.search().trim().length >= 2) this.resetAndReload();
  }

  setStatus(value: unknown): void {
    this.statusFilter.set(value as StatusFilter);
    this.resetAndReload();
  }

  setTypeFilter(value: unknown): void {
    this.typeFilter.set(value as TypeFilter);
    this.resetAndReload();
  }

  goToPrevPage(): void {
    if (!this.hasPrev() || this.loading()) return;
    this.page.update((p) => Math.max(1, p - 1));
    this.reload();
  }

  goToNextPage(): void {
    if (!this.hasNext() || this.loading()) return;
    this.page.update((p) => p + 1);
    this.reload();
  }

  private resetAndReload(): void {
    this.page.set(1);
    this.selection.clear();
    this.reload();
  }

  reload(): void {
    this.loading.set(true);
    const status = this.statusFilter();
    const type = this.typeFilter();
    const term = this.search().trim();
    const query: {
      page: number;
      pageSize: number;
      status?: SaleStatus;
      type?: 'one_time' | 'recurring';
      search?: string;
      search_field?: SalesSearchField;
      sort_by?: SaleSortBy;
      sort_order?: 'asc' | 'desc';
    } = { page: this.page(), pageSize: this.pageSize(), sort_by: this.sortBy(), sort_order: this.sortOrder() };
    if (status !== 'all') query.status = status;
    if (type !== 'all') query.type = type;
    if (term.length >= 2) {
      query.search = term;
      query.search_field = this.searchField();
    }

    this.api
      .listSales(query)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (resp) => {
          this.sales.set(resp.data);
          this.total.set(resp.meta.total);
          this.totalPages.set(resp.meta.totalPages);
          this.hasNext.set(resp.meta.hasNextPage);
          this.hasPrev.set(resp.meta.hasPreviousPage);
          this.loading.set(false);
        },
        error: () => this.loading.set(false),
      });
  }

  onColumnSort(change: UiSortChange): void {
    this.sortBy.set(change.key as SaleSortBy);
    this.sortOrder.set(change.direction);
    this.resetAndReload();
  }

  // ── Selection ──
  isSelected(id: string): boolean {
    return this.selection.isSelected(id);
  }
  toggleSelect(id: string): void {
    this.selection.toggle(id);
  }
  selectAllOnPage(checked: boolean): void {
    if (checked) this.selection.selectMany(this.sales().map((s) => s.id));
    else this.selection.clear();
  }
  selectAllMatching(): void {
    this.selection.selectAllMatching();
  }

  onBulkAction(event: UiBulkActionEvent): void {
    if (event.actionId === 'cancel') void this.bulkCancel();
    else if (event.actionId === 'export') this.exportSelected();
  }

  private saleFilters(): {
    status?: SaleStatus;
    type?: 'one_time' | 'recurring';
    search?: string;
    search_field?: SalesSearchField;
  } {
    const out: {
      status?: SaleStatus;
      type?: 'one_time' | 'recurring';
      search?: string;
      search_field?: SalesSearchField;
    } = {};
    if (this.statusFilter() !== 'all') out.status = this.statusFilter() as SaleStatus;
    if (this.typeFilter() !== 'all') out.type = this.typeFilter() as 'one_time' | 'recurring';
    const term = this.search().trim();
    if (term.length >= 2) {
      out.search = term;
      out.search_field = this.searchField();
    }
    return out;
  }

  async bulkCancel(): Promise<void> {
    const count = this.selectionCount();
    if (count === 0) return;
    const ok = await this.confirmDialog.confirm({
      title: `Cancelar ${count} venda(s)?`,
      description:
        'As vendas passam para "Cancelada" e ficam no histórico. Vendas que não permitem cancelamento (ex.: rascunho, já cancelada) serão ignoradas. Esta ação não pode ser desfeita.',
      confirmLabel: 'Cancelar vendas',
      tone: 'danger',
    });
    if (!ok) return;
    const body = this.selection.allMatching()
      ? { all: true, filters: this.saleFilters() }
      : { ids: this.selection.ids() };
    this.api
      .bulkCancelSales(body)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          const failed = result.failed.length;
          if (failed > 0) {
            this.toastr.info(`${result.affected.length} cancelada(s), ${failed} ignorada(s)/falharam.`);
          } else {
            this.toastr.success(`${result.affected.length} venda(s) cancelada(s).`);
          }
          this.selection.clear();
          this.reload();
        },
        error: (err) => this.toastr.error('Não foi possível cancelar em massa.', err?.error?.message),
      });
  }

  exportSelected(): void {
    const params: Record<string, string | undefined> = this.selection.allMatching()
      ? {
          status: this.statusFilter() === 'all' ? undefined : this.statusFilter(),
          type: this.typeFilter() === 'all' ? undefined : this.typeFilter(),
          search: this.saleFilters().search,
          search_field: this.saleFilters().search_field,
        }
      : { ids: this.selection.ids().join(',') };
    this.api
      .exportSales(params)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (blob) => this.downloadBlob(blob, 'vendas.csv'),
        error: () => this.toastr.error('Não foi possível exportar.'),
      });
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  statusLabel(status: SaleStatus): string {
    return STATUS_LABEL_PT_BR[status];
  }

  saleStatusTone(status: SaleStatus): BadgeTone {
    if (status === 'active' || status === 'trialing') return 'success';
    if (status === 'pending' || status === 'paused' || status === 'past_due') return 'warning';
    if (status === 'cancelled' || status === 'refunded' || status === 'expired') return 'danger';
    return 'neutral';
  }

  formatMoney(total: string, currency: string): string {
    const value = Number(total);
    if (!Number.isFinite(value)) return total;
    try {
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency }).format(value);
    } catch {
      return `${total} ${currency}`;
    }
  }

  formatDate(iso: string): string {
    try {
      return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(iso));
    } catch {
      return iso;
    }
  }

  canEdit(sale: SaleListItem): boolean {
    if (sale.managementType !== 'platform_managed') return false;
    return ['draft', 'pending', 'active', 'trialing'].includes(sale.status);
  }

  canDelete(sale: SaleListItem): boolean {
    return sale.managementType === 'platform_managed' && sale.status === 'draft';
  }

  canCancel(sale: SaleListItem): boolean {
    if (sale.managementType !== 'platform_managed') return false;
    return ['pending', 'active', 'trialing', 'paused', 'past_due'].includes(sale.status);
  }

  async confirmDelete(sale: SaleListItem): Promise<void> {
    const ok = await this.confirmDialog.confirm({
      title: 'Excluir rascunho?',
      description: 'Esta ação é definitiva. O rascunho será removido permanentemente.',
      confirmLabel: 'Excluir',
      tone: 'danger',
    });
    if (!ok) return;
    this.api.deleteSale(sale.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.sales.update((arr) => arr.filter((s) => s.id !== sale.id));
        this.total.update((t) => Math.max(0, t - 1));
        this.toastr.success('Rascunho excluído.');
      },
      error: (err) =>
        this.toastr.error('Não foi possível excluir.', err?.error?.message ?? 'Tente novamente.'),
    });
  }

  async confirmCancel(sale: SaleListItem): Promise<void> {
    const ok = await this.confirmDialog.confirm({
      title: 'Cancelar venda?',
      description:
        'A venda passa para o status "Cancelada" e fica registrada no histórico. Esta ação não pode ser desfeita.',
      confirmLabel: 'Cancelar venda',
      tone: 'danger',
    });
    if (!ok) return;
    this.api.cancelSale(sale.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.toastr.success('Venda cancelada.');
        this.reload();
      },
      error: (err) =>
        this.toastr.error('Não foi possível cancelar.', err?.error?.message ?? 'Tente novamente.'),
    });
  }
}
